import type { ReactNode, CSSProperties } from 'react';
import { TwDataTable } from './TwDataTable';

/**
 * The app's list-table façade. Now a thin adapter over the fully-Tailwind
 * `TwDataTable` (no Ant Design) — TwDataTable owns the toolbar (density +
 * column-visibility, persisted per `storageKey`), client-side sorting, and both
 * client + server (controlled `current`/`total`) pagination. The public
 * `DataTableColumn` / `DataTable` API is unchanged, so consuming pages are untouched.
 */
export interface DataTableColumn<T> {
  /** Stable key. If absent, uses dataIndex string. */
  key?: string;
  title: ReactNode;
  dataIndex?: string | string[];
  render?: (value: any, record: T, index: number) => ReactNode;
  width?: number | string;
  align?: 'left' | 'right' | 'center';
  fixed?: 'left' | 'right';
  /** Set to true to add ASC/DESC sorter (client-side by default). */
  sortable?: boolean;
  /** Custom sorter — overrides default. Return -1/0/+1. */
  sorter?: (a: T, b: T) => number;
  /** If false, hidden by default in the column picker. */
  defaultVisible?: boolean;
  /** Hide this column from the picker entirely (always shown). */
  alwaysVisible?: boolean;
  /** Truncate overflow to one line with "…" and show full text on hover. */
  ellipsis?: boolean;
}

interface DataTablePagination {
  pageSize?: number;
  current?: number;
  total?: number;
  showSizeChanger?: boolean;
  pageSizeOptions?: (number | string)[];
  hideOnSinglePage?: boolean;
  showTotal?: (total: number, range: [number, number]) => ReactNode;
  onChange?: (page: number, pageSize: number) => void;
}

interface DataTableProps<T> {
  /** Stable id used to persist column-visibility + density in localStorage. */
  storageKey: string;
  columns: DataTableColumn<T>[];
  dataSource?: T[];
  rowKey?: string | ((r: T, index?: number) => string);
  loading?: boolean;
  pagination?: false | DataTablePagination;
  expandable?: any;
  onRow?: (record: T, index?: number) => { onClick?: (e?: any) => void; style?: CSSProperties; className?: string } | undefined;
  rowClassName?: (record: T, index: number) => string;
  scroll?: { x?: number | string | true; y?: number | string };
  size?: 'small' | 'middle' | 'large';
  bordered?: boolean;
  /** Free-form toolbar items rendered on the LEFT (search / filters). */
  toolbarLeft?: ReactNode;
  /** Free-form toolbar items rendered on the RIGHT (export / add). */
  toolbarRight?: ReactNode;
  emptyText?: ReactNode;
  locale?: { emptyText?: ReactNode };
}

export function DataTable<T extends object>({
  storageKey, columns, dataSource = [], rowKey = 'key', loading, pagination,
  expandable, onRow, rowClassName, toolbarLeft, toolbarRight, emptyText, locale, bordered = true,
}: DataTableProps<T>) {
  const pg =
    pagination === false
      ? (false as const)
      : {
          pageSize: pagination?.pageSize,
          showSizeChanger: pagination?.showSizeChanger,
          pageSizeOptions: pagination?.pageSizeOptions?.map(Number),
          hideOnSinglePage: pagination?.hideOnSinglePage,
          showTotal: pagination?.showTotal,
          current: pagination?.current,
          total: pagination?.total,
          onChange: pagination?.onChange,
        };

  return (
    <TwDataTable
      storageKey={storageKey}
      columns={columns as any}
      dataSource={dataSource as any}
      rowKey={rowKey as any}
      loading={loading}
      pagination={pg}
      expandable={expandable}
      onRow={onRow as any}
      rowClassName={rowClassName ? (r: any) => rowClassName(r, 0) : undefined}
      toolbarLeft={toolbarLeft}
      toolbarRight={toolbarRight}
      emptyText={emptyText ?? locale?.emptyText}
      bordered={bordered}
    />
  );
}
